import React from "react";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { motion } from "framer-motion";
import ScrollSuggestBase from "./ScrollSuggest";
import DotsBase from "./Dots";
import trimLeading from "@src/utils/trimLeading";
import settings from "@src/settings";
import getGridHeight from "@src/utils/getGridHeight";
import { down, only, up } from "styled-breakpoints";

const heroSettings = {
	gutterWidth: "4rem",
};
export const Frame = styled.div<{
	heroHeight?: number;
}>`
	display: flex;
	position: relative;
	width: 100%;
	height: ${(props) => props.heroHeight}px;
	min-height: ${getGridHeight({ rows: 30 })}px;
	padding: ${getGridHeight({ rows: 4, withTopGap: true })}px
		${heroSettings.gutterWidth};
	&:after {
		content: "";
		display: block;
		position: absolute;
		bottom: 0;
		top: 50%;
		left: 0;
		z-index: -1;
		width: 100%;
		background-color: ${settings.color.pale};
	}
	${only("lg")} {
		min-height: ${getGridHeight({ rows: 32 })}px;
	}
	${down("md")} {
		min-height: inherit;
	}
	${only("sm")} {
		padding: ${getGridHeight({ rows: 4 })}px 2rem
			${getGridHeight({ rows: 3 })}px;
	}
	${down("xs")} {
		max-height: ${getGridHeight({ rows: 30 })}px;
		padding: 1rem 0.5rem ${getGridHeight({ rows: 2, withTopGap: true })}px;
	}
`;

export const Container = styled.div`
	flex: 1 1 auto;
	align-self: stretch;
	display: flex;
	position: relative;
	width: 100%;
	max-width: 1200px;
	margin: 0 auto;
	z-index: 0;
	${down("lg")} {
		flex-direction: column-reverse;
	}
	${down("md")} {
		height: 100%;
	}
`;

export const Lead = styled.div`
	flex: 0 0 26rem;
	max-width: 26rem;
	display: flex;
	flex-direction: column;
	justify-content: center;
	background-color: ${settings.color.darken};
	border-radius: ${settings.radius.medium}px 0 0 ${settings.radius.medium}px;
	${down("lg")} {
		border-radius: 0 0 ${settings.radius.medium}px ${settings.radius.medium}px;
		max-width: inherit;
		flex: 0 0 auto;
	}
	${down("sm")} {
		border-radius: 0 0 ${settings.radius.small}px ${settings.radius.small}px;
	}
`;
export const Text = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 0 6rem;
	${down("lg")} {
		padding: 0 4rem 4rem;
	}
	${down("md")} {
		padding: 0 3rem 3rem;
	}
	${down("xs")} {
		padding: 0 2rem 2rem;
	}
`;
export const ImgBox = styled.div`
	flex: auto 1 1;
	background-color: #ddd;
	border-radius: 0 ${settings.radius.medium}px ${settings.radius.medium}px 0;
	z-index: -1;
	position: relative;
	overflow: hidden;
	${down("lg")} {
		border-radius: ${settings.radius.medium}px ${settings.radius.medium}px 0 0;
		flex: 1 1 ${getGridHeight({ rows: 15 })}px;
	}
	${down("md")} {
		border-radius: ${settings.radius.small}px ${settings.radius.small}px 0 0;
	}
`;
export const ImgItem = styled(motion.div)`
	display: block;
	position: absolute;
	width: 120%;
	height: 100%;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;

export const Title = styled.h1`
	flex: auto 0 0;
	font-family: "Montserrat", serif;
	font-weight: 800;
	color: #ffffff;
	letter-spacing: 5.51px;
	white-space: pre-wrap;
	width: 50rem;
	word-wrap: break-word;
	${trimLeading({ fontSize: 64, lineHeight: 84, font: "montserrat" })}
	${down("lg")} {
		margin-top: -12rem;
	}
	${down("md")} {
		${trimLeading({ fontSize: 56, lineHeight: 68, font: "montserrat" })}
		width: auto;
		hyphens: auto;
		position: relative;
		margin-top: -12rem;
	}
	${down("sm")} {
		${trimLeading({ fontSize: 35, lineHeight: 35, font: "montserrat" })}
		margin-top: -5rem;
	}
`;

export const SubTitle = styled.h2<{ spaceForTitle: number }>`
	flex: auto 0 0;
	font-weight: 500;
	font-family: "Noto Sans JP", sans-serif;
	color: #ffffff;
	letter-spacing: 2.7px;
	${trimLeading({ fontSize: 18, lineHeight: 34 })}
	${Title} + & {
		margin-top: 40px;
	}
	${down("lg")} {
		${Title} + & {
			margin-top: 3rem;
		}
	}
	${down("md")} {
		${trimLeading({ fontSize: 16 * (8 / 7), lineHeight: 32 })}
		${Title} + & {
			margin-top: 1.5rem;
		}
	}
`;

export const Caption = styled.p`
	flex: auto 0 0;
	font-weight: 400;
	font-family: "Noto Sans JP", sans-serif;
	color: #ffffff;
	letter-spacing: 2.2857px;
	${trimLeading({ fontSize: 16, lineHeight: 32 })}
	${SubTitle} + &{
		margin-top: 48px;
	}
	${down("md")} {
		${SubTitle} + &{
			margin-top: 1.5rem;
		}
	}
}
`;
export const CopyRight = styled.p`
	position: absolute;
	left: calc(${heroSettings.gutterWidth} / 2 * -1);
	top: 50%;
	transform: translate(-50%, -50%) rotate(90deg);
	font-family: ${settings.font.en};
	font-weight: 600;
	font-size: 12px;
	line-height: 1;
	letter-spacing: 0.11em;
	color: ${settings.color.main};
	${down("sm")} {
		display: none;
	}
`;

export const ToCorporate = styled.a`
	text-decoration: none;
	position: absolute;
	right: calc(${heroSettings.gutterWidth} / 2 * -1);
	top: 50%;
	transform: translate(50%, -50%) rotate(-90deg);
	font-weight: 700;
	font-family: ${settings.font.jp};
	color: ${settings.color.main};
	font-size: 14px;
	letter-spacing: 1.4px;
	line-height: 1;
	padding-bottom: 10px;
	border-bottom: 2px solid ${settings.color.main};
	${down("sm")} {
		display: none;
	}
`;
export const ScrollSuggest = styled(ScrollSuggestBase)`
	position: absolute;
	bottom: -56px;
	left: 3rem;
	${down("md")} {
		left: 1.5rem;
		bottom: ${getGridHeight({
			rows: 1,
			withBottomGap: true,
		}) * -1}px;
	}
	${down("xs")} {
		left: 1rem;
	}
`;

export const Dots = styled(DotsBase)`
	position: absolute;
	bottom: -56px;
	right: 0;
	${down("sm")} {
		bottom: ${getGridHeight({
			rows: 1,
			withBottomGap: true,
		}) * -1}px;
	}
`;
